import { useCallback, useEffect, useState } from 'react';
import { api, ErrorApi, type EnlacePantalla } from '../api';
import { fechaCorta, describirVigencia } from '../enlaces';
import { Tabla } from './Tabla';

/**
 * Enlaces públicos a la pantalla de alertas graves.
 *
 * No tiene nada que ver con quién recibe los correos de arriba: esto es una
 * URL para dejar abierta en un monitor —una sala de operaciones, una TV—, que
 * se actualiza sola y no manda nada a nadie. Abre sin sesión, así que la URL
 * se muestra una sola vez, igual que el enlace al informe.
 */
export function Pantallas({ onError }: { onError: (err: unknown) => void }) {
  const [enlaces, setEnlaces] = useState<EnlacePantalla[] | null>(null);
  const [nuevo, setNuevo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setEnlaces((await api.enlacesPantalla()).enlaces);
    } catch (err) {
      onError(err);
    }
  }, [onError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function crear(): Promise<void> {
    setCreando(true);
    setError(null);
    setNuevo(null);
    try {
      const creado = await api.crearEnlacePantalla();
      setNuevo(creado.url);
      await cargar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo crear la pantalla');
    } finally {
      setCreando(false);
    }
  }

  async function revocar(id: string): Promise<void> {
    setError(null);
    try {
      await api.revocarEnlacePantalla(id);
      setNuevo(null);
      await cargar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo revocar');
    }
  }

  if (!enlaces) return null;

  return (
    <section className="enlaces-informe">
      <div className="analisis-cabecera">
        <h3>Pantallas de alertas</h3>
        <button onClick={() => void crear()} disabled={creando}>
          {creando ? 'Creando…' : 'Crear pantalla'}
        </button>
      </div>

      <p className="tenue">
        Abre sin pedir sesión y muestra solo lo grave, pensada para dejarla abierta en un monitor.
        Se actualiza sola cada treinta segundos y no envía nada a nadie. Se puede revocar acá.
      </p>

      {error && <p className="error">{error}</p>}

      {nuevo && (
        <div className="secreto-nuevo">
          <h4>Copia el enlace ahora</h4>
          <p className="tenue">Es la única vez que se muestra. Si lo pierdes, revocá y creá otro.</p>
          <code>{nuevo}</code>
          <button onClick={() => void navigator.clipboard?.writeText(nuevo)}>Copiar</button>
        </div>
      )}

      {enlaces.length === 0 ? (
        <p className="vacio">No hay pantallas vigentes.</p>
      ) : (
        <Tabla className="salud">
          <thead>
            <tr>
              <th>Creado por</th>
              <th>Vence</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {enlaces.map((e) => (
              <tr key={e.id}>
                <td>
                  {e.creadoPor}
                  <span className="tenue"> · {fechaCorta(e.creado)}</span>
                </td>
                <td>{describirVigencia(e.expira)}</td>
                <td className="num">
                  <button className="secundario" onClick={() => void revocar(e.id)}>
                    Revocar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}
    </section>
  );
}
